import { useEffect, useMemo, useState } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import Modal from "@/components/common/Modal";
import GestionBalancesPanel from "@/components/financiero/GestionBalancesPanel";
import { useSession } from "@/context/SessionProvider";
import { useEmpresasResumen } from "./resumen/useEmpresasResumen";
import EmpresaCard from "./resumen/EmpresaCard";

async function fetchFechaCorte() {
    try {
        const response = await axios.get(API_ROUTES.fechaCorte);
        return response.data;
    } catch (error) {
        console.error('Error al obtener fecha de corte:', error);
        const ayer = new Date();
        ayer.setDate(ayer.getDate() - 1);
        return {
            fecha_corte: format(ayer, 'yyyy-MM-dd'),
            tiene_datos: false
        };
    }
}

/**
 * Componente principal para mostrar resumen financiero por empresa
 */
export default function ResumenPorEmpresa({ modo = "empresa" }) {
    const { user, loading: sessionLoading } = useSession();
    const navigate = useNavigate();

    // Estado para fecha de corte desde backend
    const [fechaCorteData, setFechaCorteData] = useState(null);
    const [fechaCorteLoading, setFechaCorteLoading] = useState(true);

    // Cargar fecha de corte desde backend
    useEffect(() => {
        let mounted = true;

        async function loadFechaCorte() {
            setFechaCorteLoading(true);
            const data = await fetchFechaCorte();
            if (mounted) {
                setFechaCorteData(data);
                setFechaCorteLoading(false);
            }
        }

        loadFechaCorte();

        return () => {
            mounted = false;
        };
    }, []);

    // Fecha formateada para mostrar en la UI
    const fechaCorte = useMemo(() => {
        if (!fechaCorteData?.fecha_corte) return '...';

        try {
            const fecha = parseISO(fechaCorteData.fecha_corte);
            return format(fecha, "dd-MM-yyyy", { locale: es });
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return fechaCorteData.fecha_corte;
        }
    }, [fechaCorteData]);

    // Permisos
    const canSeeTarifas = useMemo(() => {
        const isAdmin =
            !!(user?.permisos?.includes?.("noc_admin")) ||
            user?.rol === "noc_admin" ||
            user?.perfil?.clave === "noc_admin" ||
            [1, 5].includes(Number(user?.perfil_id));
        return modo === "admin" || isAdmin;
    }, [user, modo]);

    // Hook personalizado para lógica de empresas
    const {
        empresas,
        setEmpresas,
        loading,
        setLoading,
        isNocAdmin,
        setIsNocAdmin,
        fetchResumenEmpresa,
        prefetchSummaries,
        refreshEmpresaResumen,
        computeIsNocAdmin,
        bootPrefetchedRef,
    } = useEmpresasResumen(user, sessionLoading, modo, canSeeTarifas);

    // Estados locales
    const [query, setQuery] = useState("");
    const [modal, setModal] = useState({ open: false, type: null, empresa: null });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const closeModal = () => setModal({ open: false, type: null, empresa: null });

    // Resetear página al buscar
    useEffect(() => {
        setPage(1);
    }, [query]);

    // Handler para refresh tras aplicar cambios en balance
    const handleApplied = async (empresa_id) => {
        try {
            await refreshEmpresaResumen(empresa_id);
            closeModal();
        } catch (e) {
            console.error("Error refrescando tras aplicar:", e);
        }
    };

    // Boot: Cargar datos iniciales
    useEffect(() => {
        if (sessionLoading) return;
        (async () => {
            setLoading(true);
            try {
                if (!user) {
                    setEmpresas([]);
                    return;
                }
                setIsNocAdmin(computeIsNocAdmin(user));

                if (modo === "empresa" && Number(user?.perfil_id) !== 1) {
                    // Modo empresa individual
                    const empresa_id = user?.empresa_id;
                    if (!empresa_id) throw new Error("No se pudo obtener la empresa del usuario");
                    const resumen = await fetchResumenEmpresa(empresa_id, { includeTarifas: canSeeTarifas });
                    const displayName = resumen?.empresa_nombre || user?.empresa_nombre || `Empresa`;
                    setEmpresas([
                        {
                            id: empresa_id,
                            nombre: displayName,
                            open: true,
                            resumen,
                            clientesOpen: {},
                            troncales: {},
                        },
                    ]);
                } else {
                    // Modo admin: cargar todas las empresas
                    const { data } = await axios.get(API_ROUTES.listadoEmpresas || API_ROUTES.empresasList, {
                        withCredentials: true,
                    });
                    const list = Array.isArray(data) ? data : [];
                    const rows = list.map((emp) => ({
                        id: emp.id ?? emp.empresa_id,
                        nombre: emp.nombre ?? emp.nombre_empresa ?? `Empresa ${emp.id ?? emp.empresa_id}`,
                        open: false,
                        resumen: null,
                        loadingResumen: false,
                        clientesOpen: {},
                        troncales: {},
                    }));
                    setEmpresas(rows);

                    // 🚀 Prefetch optimizado: primeras 50 empresas
                    if (!bootPrefetchedRef.current) {
                        bootPrefetchedRef.current = true;
                        const visibleRows = rows.slice(0, 50);  // Aumentado a 50
                        setTimeout(() => prefetchSummaries(visibleRows, 5, canSeeTarifas), 50);
                    }
                }
            } catch (e) {
                console.error("Error en boot:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [sessionLoading, user, modo, canSeeTarifas]);

    // Listener para evento personalizado de cambios en finanzas
    useEffect(() => {
        const handleFinanzasChanged = (event) => {
            const { empresa_id } = event.detail || {};
            if (!empresa_id) return;
            refreshEmpresaResumen(empresa_id);
        };

        window.addEventListener("finanzas-changed", handleFinanzasChanged);
        return () => window.removeEventListener("finanzas-changed", handleFinanzasChanged);
    }, [empresas]);

    // Toggle empresa
    const toggleEmpresaById = async (empresaId) => {
        const i = empresas.findIndex((e) => e.id === empresaId);
        if (i === -1) return;

        const row = empresas[i];
        const willOpen = !row.open;

        if (willOpen && !row.resumen) {
            const next = [...empresas];
            next[i] = { ...row, open: true, loadingResumen: true };
            setEmpresas(next);
            try {
                const resumen = await fetchResumenEmpresa(row.id, { includeTarifas: canSeeTarifas });
                setEmpresas((prev) => {
                    const cp = [...prev];
                    cp[i] = { ...cp[i], resumen, loadingResumen: false };
                    return cp;
                });
            } catch (e) {
                console.error("No se pudo obtener el resumen:", e);
                setEmpresas((prev) => {
                    const cp = [...prev];
                    cp[i] = { ...cp[i], loadingResumen: false };
                    return cp;
                });
            }
        } else {
            const next = [...empresas];
            next[i] = { ...row, open: willOpen };
            setEmpresas(next);
        }
    };

    // Toggle cliente (para expandir troncales)
    const toggleClienteById = async (empresaId, clienteId) => {
        const i = empresas.findIndex((e) => e.id === empresaId);
        if (i === -1) return;

        const row = empresas[i];
        const isOpen = !!row.clientesOpen?.[clienteId];
        const next = [...empresas];
        next[i] = {
            ...row,
            clientesOpen: { ...(row.clientesOpen || {}), [clienteId]: !isOpen },
        };
        setEmpresas(next);

        if (!isOpen && !row.troncales?.[clienteId]) {
            try {
                const url = API_ROUTES.troncalesPorCliente
                    ? API_ROUTES.troncalesPorCliente(row.id, clienteId)
                    : `${API_ROUTES.monitorDetalle}?empresa_id=${row.id}&cliente_id=${clienteId}`;
                const { data } = await axios.get(url, { withCredentials: true });
                const list = Array.isArray(data)
                    ? data.map((t) => ({
                        id: t.id ?? t.troncal_id ?? t.sip_id ?? t.name,
                        nombre: t.nombre ?? t.name ?? `Troncal #${t.id ?? t.troncal_id ?? ""}`,
                    }))
                    : [];
                setEmpresas((prev) => {
                    const cp = [...prev];
                    const r = cp[i];
                    cp[i] = {
                        ...r,
                        troncales: { ...(r.troncales || {}), [clienteId]: list },
                    };
                    return cp;
                });
            } catch (e) {
                console.error("No se pudieron cargar troncales:", e);
            }
        }
    };

    // Filtrar empresas
    const empresasFiltradas = useMemo(() => {
        if (modo !== "admin") return empresas;
        const t = (query || "").trim().toLowerCase();
        if (!t) return empresas;
        return empresas.filter((e) => (e.nombre || "").toLowerCase().includes(t));
    }, [empresas, query, modo]);

    // Paginación
    const total = empresasFiltradas.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const pageEmpresas = useMemo(() => {
        if (modo !== "admin") return empresasFiltradas;
        const start = (page - 1) * pageSize;
        return empresasFiltradas.slice(start, start + pageSize);
    }, [empresasFiltradas, page, pageSize, modo]);

    // Handlers para navegación y modal
    const handleNavigateTransactions = (empresaId, empresaNombre) => {
        if (modo === "admin" && isNocAdmin) {
            navigate(`/admin/finanzas/empresa/${empresaId}/resumen`, {
                state: { empresaNombre },
            });
        } else {
            navigate("/resumenes");
        }
    };

    const handleOpenBalanceModal = (empresaId) => {
        setModal({
            open: true,
            type: "balances",
            empresa: { empresa_id: empresaId },
        });
    };

    // Componente de paginación
    const Paginacion = () => (
        <div className="flex items-center justify-between px-2 pt-1 text-xs text-slate-600 overflow-x-auto">
            <div className="flex items-center gap-2">
                <span className="whitespace-nowrap">
                    Mostrando <strong>{total ? (page - 1) * pageSize + 1 : 0}–{Math.min(page * pageSize, total)}</strong> de{" "}
                    <strong>{total}</strong>
                </span>

                <label className="inline-flex items-center gap-1">
                    <span>Tamaño:</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                        }}
                        className="border rounded px-1 py-0.5"
                    >
                        {[10, 20, 50, 100].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="flex items-center gap-1">
                <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={() => setPage(1)} disabled={page <= 1} type="button">
                    «
                </button>
                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    type="button"
                >
                    ‹
                </button>

                <span className="px-2 whitespace-nowrap">
                    Página{" "}
                    <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={page}
                        onChange={(e) => {
                            const v = Number(e.target.value || 1);
                            setPage(Math.min(Math.max(1, v), totalPages));
                        }}
                        className="w-14 border rounded px-1 py-0.5 text-right"
                    />{" "}
                    / {totalPages}
                </span>

                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    type="button"
                >
                    ›
                </button>
                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    type="button"
                >
                    »
                </button>
            </div>
        </div>
    );

    if (loading) return <div className="text-sm text-gray-500">Cargando…</div>;

    return (
        <div className="space-y-3">
            {/* Barra de búsqueda y paginación (solo admin) */}
            {modo === "admin" && (
                <div className="sticky top-0 z-10">
                    <div className="p-2">
                        <div className="relative max-w-xl">
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.key === "Escape" && setQuery("")}
                                placeholder="Buscar empresa..."
                                className="w-full rounded-lg border border-slate-200 pl-10 pr-8 py-2 text-xs bg-transparent focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">🔍</span>
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full w-6 h-6 flex items-center justify-center text-lg font-bold"
                                    type="button"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                    <Paginacion />
                </div>
            )}

            {/* Header de columnas (solo admin) */}
            {modo === "admin" && (
                <div className="w-full flex justify-end mb-1">
                    <div className="grid grid-cols-6 gap-2 w-[900px] max-w-[85vw] pr-1">
                        <div className="text-sm font-semibold text-slate-500 text-center">Cargos</div>
                        <div className="text-sm font-semibold text-slate-500 text-center">Depósitos</div>
                        <div className="text-sm font-semibold text-slate-500 text-center">Balance</div>
                        <div className="text-sm font-semibold text-slate-500 text-center">Crédito</div>
                        <div className="text-sm font-semibold text-slate-500 text-center"></div>
                        <div className="text-sm font-semibold text-slate-500 text-center"></div>
                    </div>
                </div>
            )}

            {/* Lista de empresas */}
            {pageEmpresas.map((row) => (
                <EmpresaCard
                    key={row.id}
                    empresa={row}
                    modo={modo}
                    isNocAdmin={isNocAdmin}
                    canSeeTarifas={canSeeTarifas}
                    fechaCorte={fechaCorte}
                    onToggleEmpresa={toggleEmpresaById}
                    onNavigateTransactions={handleNavigateTransactions}
                    onOpenBalanceModal={handleOpenBalanceModal}
                    onToggleCliente={(clienteId) => toggleClienteById(row.id, clienteId)}
                />
            ))}

            {/* Paginación al final (solo admin con múltiples páginas) */}
            {modo === "admin" && total > pageSize && <Paginacion />}

            {/* Modal de gestión de balances */}
            <Modal open={modal.open && modal.type === "balances"} title="⚡ Gestionar Balance" onClose={closeModal} maxWidth="max-w-4xl">
                {modal.empresa && (
                    <GestionBalancesPanel
                        empresa={{
                            empresa_id: modal.empresa.empresa_id,
                            clientes: empresas.find((e) => e.id === modal.empresa.empresa_id)?.resumen?.clientes || [],
                        }}
                        onApplied={(empresa_id) => handleApplied(empresa_id)}
                        onClose={closeModal}
                    />
                )}
            </Modal>
        </div>
    );
}